/**
 * View Transitions Utilities
 *
 * Helper functions and types for working with TanStack Router's View Transitions API.
 * Provides consistent transition types across the application.
 */

import type { NavigateOptions } from "@tanstack/react-router";

/**
 * Predefined transition types for consistent animations across the app
 */
export const ViewTransitionTypes = {
	// App switching transitions
	APP_SWITCH_LEFT: ["app-switch", "slide-left"] as const,
	APP_SWITCH_RIGHT: ["app-switch", "slide-right"] as const,

	// Organization switching
	ORG_SWITCH: ["org-switch", "fade"] as const,

	// General navigation
	NAVIGATION_SLIDE: ["navigation", "slide"] as const,
	NAVIGATION_FADE: ["navigation", "fade"] as const,

	// Redirects
	REDIRECT: ["redirect", "fade"] as const,
	BLOCKED_REDIRECT: ["blocked-redirect", "fade"] as const,

	// Special pages
	NOT_FOUND_HOME: ["not-found-home", "slide-down"] as const,

	// Generic transitions
	FADE: ["fade"] as const,
	SLIDE_LEFT: ["slide-left"] as const,
	SLIDE_RIGHT: ["slide-right"] as const,
	SLIDE_UP: ["slide-up"] as const,
	SLIDE_DOWN: ["slide-down"] as const,
	SCALE: ["scale"] as const,
} as const;

/**
 * Type for view transition configurations
 */
export type ViewTransitionType =
	(typeof ViewTransitionTypes)[keyof typeof ViewTransitionTypes];

/**
 * Helper to create view transition options for spreading into NavigateOptions
 *
 * @example
 * ```ts
 * router.navigate({
 *   to: "/home",
 *   ...viewTransition(ViewTransitionTypes.FADE)
 * });
 * ```
 */
export function viewTransition(types: ViewTransitionType | string[]) {
	return {
		viewTransition: {
			types: Array.isArray(types) ? types : [...types],
		},
	} as const;
}

/**
 * @deprecated Use `viewTransition()` instead for better type safety
 * Helper to create NavigateOptions with view transitions
 *
 * @example
 * ```ts
 * router.navigate(withViewTransition({ to: "/home" }, ViewTransitionTypes.FADE));
 * ```
 */
export function withViewTransition<TTo extends NavigateOptions>(
	options: TTo,
	types: ViewTransitionType | string[],
): TTo {
	return {
		...options,
		...viewTransition(types),
	};
}

/**
 * Check if the browser supports view transitions
 */
export function supportsViewTransitions(): boolean {
	return (
		typeof document !== "undefined" &&
		"startViewTransition" in document &&
		typeof document.startViewTransition === "function"
	);
}

/**
 * @deprecated Use conditional spread with `viewTransition()` instead
 * Helper to conditionally apply view transitions based on browser support
 *
 * @example
 * ```ts
 * router.navigate({
 *   to: "/home",
 *   ...(supportsViewTransitions() && viewTransition(ViewTransitionTypes.FADE))
 * });
 * ```
 */
export function maybeViewTransition<TTo extends NavigateOptions>(
	options: TTo,
	types: ViewTransitionType | string[],
): TTo {
	if (!supportsViewTransitions()) {
		return options;
	}

	return withViewTransition(options, types);
}

/**
 * Check if user prefers reduced motion
 */
export function prefersReducedMotion(): boolean {
	if (typeof window === "undefined") return false;

	return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * @deprecated Use conditional spread with `viewTransition()` instead
 * Helper to respect user's motion preferences
 * Only applies transitions if user doesn't prefer reduced motion
 *
 * @example
 * ```ts
 * router.navigate({
 *   to: "/home",
 *   ...(!prefersReducedMotion() && viewTransition(ViewTransitionTypes.SLIDE_LEFT))
 * });
 * ```
 */
export function respectMotionPreference<TTo extends NavigateOptions>(
	options: TTo,
	types: ViewTransitionType | string[],
): TTo {
	if (prefersReducedMotion()) {
		return options;
	}

	return withViewTransition(options, types);
}

/**
 * Create custom view transition types
 *
 * @example
 * ```ts
 * const customTransition = createViewTransitionTypes("custom", "my-animation");
 * router.navigate(withViewTransition({ to: "/home" }, customTransition));
 * ```
 */
export function createViewTransitionTypes(...types: string[]): string[] {
	return types;
}

/**
 * Combine multiple transition types
 *
 * @example
 * ```ts
 * const combined = combineTransitionTypes(
 *   ViewTransitionTypes.FADE,
 *   createViewTransitionTypes("custom-type")
 * );
 * ```
 */
export function combineTransitionTypes(
	...typeArrays: (ViewTransitionType | string[])[]
): string[] {
	return typeArrays.flat() as string[];
}
